<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import UploadField from "@/components/UploadField.vue";
import { uploadImage, uploadVideo } from "@/lib/uploads";
import { fetchVideo, addVideo, editVideo } from "./api";

const route = useRoute(), router = useRouter();
const id = computed(() => route.params.id || null);
const isEdit = computed(() => !!id.value);
const loading = ref(false), notFound = ref(false), saving = ref(false), success = ref(false), error = ref(""), validationError = ref("");
let original = null;

const title = ref(""), sequence = ref(""), imageUrl = ref(""), videoUrl = ref(""), duration = ref("");
const imageUploader = (file, p) => uploadImage("videos", file, p);
const videoUploader = (file, p) => uploadVideo(file, p);

function readDuration(file) {
  const el = document.createElement("video");
  el.preload = "metadata";
  el.onloadedmetadata = () => {
    URL.revokeObjectURL(el.src);
    const total = Math.round(el.duration || 0);
    duration.value = `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
  };
  el.src = URL.createObjectURL(file);
}

onMounted(async () => {
  if (!isEdit.value) return;
  loading.value = true;
  try {
    const v = await fetchVideo(id.value);
    if (!v) { notFound.value = true; return; }
    original = v; title.value = v.title ?? ""; sequence.value = v.sequence ?? ""; imageUrl.value = v.imageUrl ?? ""; videoUrl.value = v.videoUrl ?? ""; duration.value = v.duration ?? "";
  } catch { error.value = "Couldn't load the video."; }
  finally { loading.value = false; }
});

async function save() {
  success.value = false; error.value = ""; validationError.value = "";
  if (!title.value.trim()) { validationError.value = "Enter a title."; return; }
  if (!videoUrl.value) { validationError.value = "Upload a video clip."; return; }
  const payload = { ...(original || {}), title: title.value.trim(), imageUrl: imageUrl.value, videoUrl: videoUrl.value, duration: duration.value, sequence: sequence.value === "" ? null : Number(sequence.value) };
  saving.value = true;
  try {
    if (isEdit.value) await editVideo(id.value, payload); else await addVideo(payload);
    success.value = true; setTimeout(() => router.push({ name: "videos" }), 800);
  } catch { error.value = "Error occurred. Please try again."; }
  finally { saving.value = false; }
}
</script>

<template>
  <section>
    <VBtn variant="text" prepend-icon="mdi:arrow-left" class="mb-4" @click="router.push({ name: 'videos' })">Back</VBtn>
    <h1 class="text-h5 mb-4">{{ isEdit ? "Edit Video" : "Add Video" }}</h1>
    <p v-if="loading">Loading…</p>
    <VAlert v-else-if="notFound" type="error">Video not found.</VAlert>
    <VCard v-else class="pa-4" max-width="720">
      <VAlert v-if="error" type="error" class="mb-4">{{ error }}</VAlert>
      <VAlert v-if="success" type="success" class="mb-4">Saved. Redirecting…</VAlert>
      <form @submit.prevent="save">
        <VRow>
          <VCol cols="12"><VTextField v-model="title" label="Title" /></VCol>
          <VCol cols="12"><UploadField v-model="imageUrl" kind="image" label="Cover Image" :uploader="imageUploader" /></VCol>
          <VCol cols="12">
            <UploadField v-model="videoUrl" kind="video" label="Video Clip" :uploader="videoUploader" @select="readDuration" />
            <p v-if="duration" class="text-caption text-medium-emphasis mt-1">Duration: {{ duration }}</p>
          </VCol>
          <VCol cols="12" md="6"><VTextField v-model="sequence" label="Sequence" type="number" /></VCol>
        </VRow>
        <VAlert v-if="validationError" type="warning" class="mt-4 mb-2">{{ validationError }}</VAlert>
        <div class="d-flex gap-2 mt-4">
          <VBtn type="submit" :loading="saving">{{ isEdit ? "Update" : "Add Video" }}</VBtn>
          <VBtn variant="tonal" @click="router.push({ name: 'videos' })">Cancel</VBtn>
        </div>
      </form>
    </VCard>
  </section>
</template>
